import { useNavigation } from '#app/hooks/useNavigation.js';
import { useRouter } from '#app/hooks/useRouter.js';
import { type To } from '#app/types/router.js';
import {
  type ComponentPropsWithoutRef,
  forwardRef,
  type MouseEvent,
  type Ref,
  useCallback,
} from 'react';

export type LinkProps = Omit<ComponentPropsWithoutRef<'a'>, 'href'> & {
  readonly children: React.ReactNode;
  readonly exact?: boolean;
  readonly onClick?: (event: React.MouseEvent<HTMLAnchorElement>) => void;
  readonly onMouseEnter?: (event: React.MouseEvent<HTMLAnchorElement>) => void;
  readonly onMouseLeave?: (event: React.MouseEvent<HTMLAnchorElement>) => void;
  readonly onMouseOut?: (event: React.MouseEvent<HTMLAnchorElement>) => void;
  readonly onMouseOver?: (event: React.MouseEvent<HTMLAnchorElement>) => void;
  readonly referrerPolicy?: 'no-referrer';
  readonly rel?: string;
  readonly role?: string;
  readonly target?: string;
  readonly to: string | To;
};

/**
 * The number representing the primary mouse button in a MouseEvent.button.
 * @see https://developer.mozilla.org/en-US/docs/Web/API/MouseEvent/button#return_value
 */
const MAIN_MOUSE_BUTTON = 0;

const isModifiedEvent = (event: MouseEvent<HTMLElement>): boolean => {
  return Boolean(
    event.metaKey || event.altKey || event.ctrlKey || event.shiftKey,
  );
};

const shouldNavigate = (event: MouseEvent<HTMLAnchorElement>): boolean => {
  return (
    // Default prevented indicates another custom handler has already handled the event.
    !event.defaultPrevented &&
    // We only want to perform an action on primary mouse clicks.
    event.button === MAIN_MOUSE_BUTTON &&
    // Let browser handle links with targets other than _self.
    (!event.currentTarget.target || event.currentTarget.target === '_self') &&
    // Let browser handle new window/tab, download and context menu events.
    !isModifiedEvent(event)
  );
};

export const Link = forwardRef(
  (
    { children, className, exact, onClick, to, ...props }: LinkProps,
    ref: Ref<HTMLAnchorElement>,
  ) => {
    const { isActive } = useRouter();

    const { push, replace } = useNavigation();

    const toIsActive = isActive(to, exact);

    const handleOnClick = useCallback(
      (event: MouseEvent<HTMLAnchorElement>) => {
        try {
          onClick?.(event);
        } catch (error) {
          event.preventDefault();
          throw error;
        }

        if (to.startsWith('http://') || to.startsWith('https://')) {
          push(to.toString());

          return;
        }

        if (shouldNavigate(event)) {
          event.preventDefault();

          if (isActive(to, true)) {
            replace(to);
          } else {
            push(to);
          }
        }
      },
      [isActive, onClick, push, replace, to],
    );

    return (
      <a
        ref={ref}
        {...props}
        aria-current={toIsActive ? 'page' : undefined}
        className={className}
        href={to}
        onClick={handleOnClick}
      >
        {children}
      </a>
    );
  },
);
